import { usePermissions } from '@my/directory-api/hooks';
import type { OrderCheckpoints } from '@my/tms-api';
import { useOrderCheckpoints } from '@my/tms-api/hooks';
import { cn } from '@my/ui';
import { Check, ListChecks } from 'lucide-react';
import { useState } from 'react';
import { OrderCheckpointsPanel } from './OrderCheckpointsPanel';

/**
 * The order's Trip Checkpoints — a button beside History, with a count of what
 * is still open, that opens the panel from the right.
 *
 * <p>
 * The badge says how much the planner should care, worst first — red: something
 * is overdue (the order has moved past the status it was due before) or a check
 * failed; amber: something is due before the order's next status; grey: open
 * required items, nothing pressing; green ✓: nothing required is open. A
 * warning only — nothing about checkpoints stops an order.
 * </p>
 *
 * <p>
 * Only with the TMS Mobile module (the `mobile` capability), which is where
 * Trip Checkpoints live; renders nothing on an unsaved order.
 * </p>
 */
export function OrderCheckpointsButton({
  orderId,
  organisationId,
  onSendToDriver,
}: {
  orderId: string | null;
  organisationId: string | null;
  /** The panel's "Send to driver" shortcut; it closes this panel first. */
  onSendToDriver?: () => void;
}) {
  const { can } = usePermissions();
  const enabled = can('mobile') && !!orderId;
  const [open, setOpen] = useState(false);
  const query = useOrderCheckpoints(enabled ? organisationId : null, enabled ? orderId : null);

  if (!enabled) return null;

  const badge = query.data ? badgeFor(query.data) : null;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        title={badge ? `Checkpoints — ${badge.title}` : 'Checkpoints'}
        aria-label="Checkpoints"
        className="relative inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-slate-300 bg-white text-slate-600 hover:bg-slate-50 hover:text-slate-800"
      >
        <ListChecks className="h-5 w-5" />
        {badge && (
          <span
            className={cn(
              'absolute -right-1.5 -top-1.5 flex h-[1.125rem] min-w-[1.125rem] items-center justify-center rounded-full px-1 text-[10px] font-semibold text-white ring-2 ring-white',
              BADGE_STYLE[badge.tone],
            )}
          >
            {badge.tone === 'ok' ? <Check className="h-2.5 w-2.5" strokeWidth={3} /> : badge.count}
          </span>
        )}
      </button>
      <OrderCheckpointsPanel
        open={open}
        onClose={() => setOpen(false)}
        organisationId={organisationId}
        orderId={orderId}
        data={query.data}
        onSendToDriver={
          onSendToDriver
            ? () => {
                setOpen(false);
                onSendToDriver();
              }
            : undefined
        }
        loading={query.isPending}
        error={query.error}
      />
    </>
  );
}

type Tone = 'alert' | 'due' | 'open' | 'ok';

const BADGE_STYLE: Record<Tone, string> = {
  alert: 'bg-red-600',
  due: 'bg-amber-500',
  open: 'bg-slate-500',
  ok: 'bg-emerald-600',
};

/** The worst thing on the order, how many of it, and the tooltip. */
function badgeFor(data: OrderCheckpoints): { tone: Tone; count: number; title: string } {
  const s = data.summary;
  // A recorded No / Fail — from the office now, from the phone later.
  const failed = [...data.order, ...data.tasks.flatMap((t) => t.items)].filter((item) =>
    item.signOffs.some((so) => so.record?.answer === false),
  ).length;
  const parts = [
    s.overdue && `${s.overdue} overdue`,
    failed && `${failed} failed check${failed === 1 ? '' : 's'}`,
    s.dueNext && `${s.dueNext} due next`,
    s.openRequired && `${s.openRequired} required open`,
  ].filter(Boolean);
  const title = parts.length ? parts.join(', ') : 'nothing required is open';

  if (s.overdue || failed) return { tone: 'alert', count: s.overdue + failed, title };
  if (s.dueNext) return { tone: 'due', count: s.dueNext, title };
  if (s.openRequired) return { tone: 'open', count: s.openRequired, title };
  return { tone: 'ok', count: 0, title };
}
